/**
 * Frontend client for the M3X.REPORTS §1.F tiered AI insights.
 *
 * Pages call `fetchReportInsight()` after their data loads;
 * `<ReportAIInsight />` consumes the result.
 *
 * The insight always degrades to deterministic template narration
 * when no Anthropic key is available — never blocks rendering.
 */

import type {
  AIInsightTier,
  ReportAgent,
  ReportDomain,
} from "./catalog";

export interface InsightContext {
  reportId: string;
  domain: ReportDomain;
  tier: AIInsightTier;
  agent: ReportAgent;
  locale: string;
  period?: string | null;
  title: string;
  summaryFacts?: Record<string, string | number>;
  article?: string;
}

export interface InsightResult {
  narration: string;
  tierUsed: string;
  cached: boolean;
}

export async function fetchReportInsight(
  ctx: InsightContext,
  apiBase: string = "",
): Promise<InsightResult> {
  const res = await fetch(`${apiBase}/api/reports/_ai-insight`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      report_id: ctx.reportId,
      domain: ctx.domain,
      tier: ctx.tier,
      agent: ctx.agent,
      locale: ctx.locale,
      title: ctx.title,
      period: ctx.period ?? null,
      summary_facts: ctx.summaryFacts ?? {},
      article: ctx.article ?? null,
    }),
  });
  if (!res.ok) {
    throw new Error(`AI insight ${res.status}`);
  }
  const body = (await res.json()) as {
    narration: string;
    tier_used: string;
    cached: boolean;
  };
  return {
    narration: body.narration,
    tierUsed: body.tier_used,
    cached: body.cached,
  };
}
